import { useTranslations } from "adapters/i18n";
import { ConfigurationItem } from "constants/configurations";
import { useMemo } from "react";
import type { ConfigurationItemsVisibility } from "../configurations.types";

type UseConfigurationTabsProps = {
  itemsVisibility: ConfigurationItemsVisibility;
  selectedTab: ConfigurationItem;
};

export function useConfigurationTabs({
  itemsVisibility,
  selectedTab,
}: UseConfigurationTabsProps) {
  const { t } = useTranslations();

  const itemToVisibilityMap: Partial<Record<ConfigurationItem, boolean>> = {
    [ConfigurationItem.CAUSE_OF_DEATH]: itemsVisibility.showCauseOfDeath,
    [ConfigurationItem.FORMULATIONS]:
      itemsVisibility.showFormulationRegistration,
    [ConfigurationItem.HERD_EVOLUTION_AND_NUTRITION_PLAN]:
      itemsVisibility.showHerdEvolutionAndNutritionPlan,
    [ConfigurationItem.LOT_GOALS]: itemsVisibility.showLotGoal,
    [ConfigurationItem.RAIN_HISTORY]: itemsVisibility.showRainHistory,
    [ConfigurationItem.SCORE_ADJUSTMENT]: itemsVisibility.showScoreAdjustment,
    [ConfigurationItem.WEIGHT_GAIN_CURVE]: itemsVisibility.showWeightGain,
  };

  const tabs = useMemo<{ id: ConfigurationItem; label: string }[]>(
    () => [
      {
        id: ConfigurationItem.ANIMAL_CATEGORY,
        label: t("animalCategory:animal-category"),
      },
      {
        id: ConfigurationItem.CAUSE_OF_DEATH,
        label: t("livestock:cause-of-death"),
      },
      {
        id: ConfigurationItem.CORRAL_LISTING,
        label: t("locales:corral-listing"),
      },
      {
        id: ConfigurationItem.FARM_GOALS,
        label: t("locales:farm-goals"),
      },
      {
        id: ConfigurationItem.FORMULATIONS,
        label: t("formulation:formulations"),
      },
      {
        id: ConfigurationItem.HERD_EVOLUTION_AND_NUTRITION_PLAN,
        label: t("planning:herd-evolution-and-nutrition-plan"),
      },
      {
        id: ConfigurationItem.INGREDIENTS,
        label: t("ingredients:ingredients"),
      },
      {
        id: ConfigurationItem.LOT_GOALS,
        label: t("livestock:lot-goals"),
      },
      {
        id: ConfigurationItem.PLOT_LISTING,
        label: t("livestock:lot-listing"),
      },
      {
        id: ConfigurationItem.RAIN_HISTORY,
        label: t("locales:rain-history"),
      },
      {
        id: ConfigurationItem.SCORE_ADJUSTMENT,
        label: t("locales:score-adjustment"),
      },
      {
        id: ConfigurationItem.WEIGHING_BY_CATEGORY,
        label: t("animalCategory:weighing-by-category"),
      },
      {
        id: ConfigurationItem.WEIGHT_GAIN_CURVE,
        label: t("livestock:weight-gain-curve"),
      },
    ],
    [t]
  );

  const isTabVisible = (tabId: ConfigurationItem) => {
    return itemToVisibilityMap[tabId] !== false;
  };

  const sortedTabs = tabs
    .filter((tab) => isTabVisible(tab.id))
    .sort((a, b) => {
      if (a.id === selectedTab) return -1;
      if (b.id === selectedTab) return 1;
      return a.label.localeCompare(b.label);
    });

  return { tabs: sortedTabs };
}
